<script lang="ts">
  import PubSub from 'pubsub-js'
  import { onMount } from 'svelte'
  import ProfileCard from '@/components/appDriver/ProfileCard.svelte'
  import { lang } from '@/store'
  import { i18n, openDeeplink } from '@/plugins/util'
  import {
    currentPage,
    enquiryResult,
    topupPaymentGatewayResult,
    topupRequest,
    topupResult,
  } from '@/components/appDriver/store'
  import PageTopupAmount from '@/components/appDriver/topup/PageTopupAmount.svelte'
  import PageTopupHistory from '@/components/appDriver/topup/PageTopupHistory.svelte'
  import PageDriverTaxList from '@/components/appDriver/driverTax/DriverTaxList.svelte'
  import PageDriverSalaryList from '@/components/appDriver/driverSalary/DriverSalaryList.svelte'
  import { ensureDriverSalaryVisibilityChecked } from '@/components/appDriver/driverSalary/feature'
  import { isDriverSalaryMenuVisible } from '@/components/appDriver/driverSalary/store'
  import PageCoupon from '@/components/appDriver/Coupon/CouponList.svelte'
  import ExternalLoanWarning from '@/components/appDriver/ExternalLoanWarning.svelte'
  import ExternalLoanBrowser from '@/components/appDriver/ExternalLoanBrowser.svelte'
  import PageWallet from '@/components/appDriver/wallet/PageWallet.svelte'
  import PageAdsRegistration from '@/components/appDriver/adsRegistration/PageAdsRegistration.svelte'
  import { adsRegistrationText } from '@/components/appDriver/adsRegistration/translations'
  import { shouldRevealDriverToken } from '@/components/appDriver/driverTokenReveal'

  // State for external loan service
  import {
    isNewsfeedFeatureEnabled,
    showNewsfeedComingSoon,
  } from '@/components/appDriver/newsfeed/feature'
  import { getNewsPostsForDriver } from '@/components/appDriver/newsfeed/service'
  import PageNewsList from '@/components/appDriver/newsfeed/pages/PageNewsList.svelte'
  import {
    cachedNewsPosts,
    hasLoadedNewsPosts,
    readNewsPostIds,
    setCachedNewsPosts,
  } from '@/components/appDriver/newsfeed/store'

  let isNewsfeedAvailable = false
  let isCheckingNewsfeed = false
  let isShowExternalLoanPage = false
  let isShowExternalLoanBrowser = false
  let externalLoanUrl = ""
  let isCopyingToken = false
  let tokenCopyStatus: 'idle' | 'success' | 'error' = 'idle'
  let driverNameTapCount = 0

  const currentToken = new URLSearchParams(window.location.search).get('token') ?? ''
  $: visibleToken = shouldRevealDriverToken($enquiryResult?.user.uuid, driverNameTapCount)
    ? currentToken
    : ''

  function handleDriverNameTap() {
    driverNameTapCount += 1
  }

  async function copyDriverToken() {
    if (!visibleToken || isCopyingToken) return

    isCopyingToken = true
    tokenCopyStatus = 'idle'
    try {
      await navigator.clipboard.writeText(visibleToken)
      tokenCopyStatus = 'success'
    } catch {
      tokenCopyStatus = 'error'
    } finally {
      isCopyingToken = false
    }
  }

  onMount(() => {
    $currentPage = 'home'
    ensureDriverSalaryVisibilityChecked()
  })

  $: if ($hasLoadedNewsPosts) {
    isNewsfeedAvailable = true
  }

  $: unreadNewsCount = $hasLoadedNewsPosts
    ? $cachedNewsPosts.filter((post) => !$readNewsPostIds.includes(post.id)).length
    : 0

  async function loadNewsPosts(): Promise<boolean> {
    if (!isNewsfeedFeatureEnabled) {
      isNewsfeedAvailable = false
      return false
    }

    try {
      isCheckingNewsfeed = true
      const posts = await getNewsPostsForDriver()
      setCachedNewsPosts(posts)
      isNewsfeedAvailable = true
      return true
    } catch (error) {
      isNewsfeedAvailable = false
      console.error('Cannot load newsfeed list on home page', error)
      return false
    } finally {
      isCheckingNewsfeed = false
    }
  }

  function handleTopupMenu() {
    $topupRequest = {
      channel: '',
      amount: 0,
      lastAppointmentBookingId: null,
    }
    $topupPaymentGatewayResult = null
    $topupResult = null
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageTopupAmount })
  }

  function handleAdsRegisterMenu() {
    // To be implemented
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageAdsRegistration })
  }

  function handleDriverTaxMenu() {
    // To be implemented
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageDriverTaxList })
  }

  function handleDriverSalaryMenu() {
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageDriverSalaryList })
  }

  function handleCoupon() {
    // To be implemented
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageCoupon })
  }

  function handleWallet() {
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageWallet })
  }

  function handleExternalLoan() {
    const LOCA_FINA = import.meta.env.VITE_FINA_LOAN_URL || "https://fina-loan.loca.la"
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token') || ''
    externalLoanUrl = `${LOCA_FINA}?token=${encodeURIComponent(token)}&isDriver=true`
    isShowExternalLoanPage = true
  }

  // Debug-only: test whether the native app's flutter_inappwebview handler actually opens a webview
  function testNativeOpenFinaWebview() {
    const LOCA_FINA = import.meta.env.VITE_FINA_LOAN_URL || "https://fina-loan.loca.la"
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token') || ''
    const url = `${LOCA_FINA}?token=${encodeURIComponent(token)}&isDriver=true`
    openDeeplink(url)
  }

  async function handleNewsfeedMenu() {
    if (isCheckingNewsfeed) {
      return
    }

    const canOpenNewsfeed = await loadNewsPosts()

    if (!canOpenNewsfeed) {
      showNewsfeedComingSoon()
      return
    }

    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageNewsList })
  }
</script>

<div class="page flex min-h-full flex-col bg-[#f5f7fc]">
  <div class="mx-5 mt-5">
    <ProfileCard on:driverNameTap={handleDriverNameTap}>
      <div slot="home-actions" class="grid grid-cols-2 gap-3 pt-1">
        <button
          type="button"
          class="home-action-icon relative flex min-h-14 items-center gap-2 rounded-xl bg-[linear-gradient(278.43deg,#00A9F2_0.26%,#00AAFF_101.39%)] px-3 text-left text-white shadow-sm transition active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary disabled:cursor-wait disabled:opacity-60"
          disabled={isCheckingNewsfeed}
          on:click={handleNewsfeedMenu}
        >
          {#if unreadNewsCount > 0}
            <span class="absolute right-1.5 top-1.5 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#15acef]">
              {unreadNewsCount > 9 ? '9+' : unreadNewsCount}
            </span>
          {/if}
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20">
            <img src="{new URL('@/assets/driver/menu-noti.png', import.meta.url).href}" class="h-6 w-6 object-contain" alt="">
          </span>
          <span class="min-w-0">
            <span class="block text-xs font-semibold leading-4">ຂ່າວສານ ແລະ ປະກາດ</span>
          </span>
        </button>

        <button
          type="button"
          class="home-action-icon relative flex min-h-14 items-center gap-2 rounded-xl bg-[#e4fbf5] px-3 text-left text-[#177c68] shadow-sm transition active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:cursor-wait disabled:opacity-60"
          on:click={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageTopupHistory })}
        >
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-[#16ae91] shadow-sm">
            <img src="{new URL('@/assets/driver/menu-history.png', import.meta.url).href}" class="h-6 w-6 object-contain" alt="">
          </span>
          <span class="min-w-0">
            <span class="block text-xs font-semibold leading-4">ປະຫວັດການຊຳລະ</span>
          </span>
        </button>
      </div>
    </ProfileCard>
  </div>

  {#if visibleToken}
    <section class="mx-4 mb-4 rounded-lg bg-white p-4 shadow space-y-3" aria-label="Driver token">
      <label for="driver-token" class="block text-sm font-semibold">
        {i18n($lang, ['Driver token', 'ໂທເຄັນຄົນຂັບ'])}
      </label>
      <textarea
        id="driver-token"
        readonly
        rows="3"
        value={visibleToken}
        spellcheck={false}
        class="block w-full resize-none rounded-lg border-gray-300 bg-gray-50 font-mono text-xs"
      ></textarea>
      <button
        type="button"
        class="w-full rounded-lg bg-blue-primary p-2 text-center font-semibold text-white disabled:opacity-50"
        disabled={isCopyingToken}
        on:click={copyDriverToken}
      >
        {i18n($lang, ['Copy', 'ກ໋ອບປີ້'])}
      </button>
      <p role="status" class="text-sm" class:text-red-600={tokenCopyStatus === 'error'}>
        {#if tokenCopyStatus === 'success'}
          {i18n($lang, ['Copied', 'ກ໋ອບປີ້ສຳເລັດ'])}
        {:else if tokenCopyStatus === 'error'}
          {i18n($lang, ['Cannot copy automatically. Select the token and copy it manually.', 'ບໍ່ສາມາດກ໋ອບປີ້ອັດຕະໂນມັດໄດ້. ກະລຸນາເລືອກໂທເຄັນ ແລະ ກ໋ອບປີ້ເອງ.'])}
        {/if}
      </p>
      <button
        type="button"
        class="w-full rounded-lg bg-blue-dark p-2 text-center font-semibold text-white"
        on:click={testNativeOpenFinaWebview}
      >
        {i18n($lang, ['Test native open webview (Fina)', 'ທົດສອບເປີດ WebView ຜ່ານ Native (Fina)'])}
      </button>
    </section>
  {/if}

  <main class="mx-5 mt-5 space-y-5 pb-8">
    <section aria-labelledby="main-menu-heading">
      <h2 id="main-menu-heading" class="mb-3 text-sm font-semibold text-slate-600">ລະບົບຫຼັກ</h2>
      <div class="grid grid-cols-2 gap-4">
        <button
          type="button"
          class="main-menu-card payment-card flex min-h-28 flex-col items-start justify-end overflow-hidden rounded-2xl p-4 text-left text-white shadow-sm transition active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary"
          on:click={handleTopupMenu}
        >
          <span class="mb-3 flex h-14 w-14 items-center justify-center rounded-xl shadow-sm">
            <img src="{new URL('@/assets/driver/menu-payment.png', import.meta.url).href}" class="h-full w-full object-contain" alt="">
          </span>
          <span class="text-sm font-semibold">ລະບົບຊຳລະ</span>
          <span class="mt-0.5 text-[10px] text-white/75">ຈັດການການຊຳລະ</span>
        </button>

        <button
          type="button"
          class="main-menu-card wallet-card flex min-h-28 flex-col items-start justify-end overflow-hidden rounded-2xl p-4 text-left text-white shadow-sm transition active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
          on:click={handleWallet}
          h-16 w-16>
          <span class="mb-3 flex h-14 w-14 items-center justify-center rounded-xl shadow-sm">
            <img src="{new URL('@/assets/driver/menu-wallet.png', import.meta.url).href}" class="h-full w-full object-contain" alt="">
          </span>
          <span class="text-sm font-semibold">ກະເປົາເງິນຄົນຂັບ</span>
          <span class="mt-0.5 text-[10px] text-white/75">ບັນຊີຂອງຄົນຂັບ</span>
        </button>
      </div>
    </section>

    <section aria-labelledby="services-heading">
      <h2 id="services-heading" class="mb-3 text-sm font-semibold text-slate-600">ການບໍລິການ ແລະ ອື່ນໆ</h2>
      <div class="grid grid-cols-2 gap-3">
        <button type="button" class="service-card bg-white focus-visible:outline-blue-primary" on:click={handleExternalLoan}>
          <span class="service-icon bg-slate-50"><img src="{new URL('@/assets/driver/fina-logo.png', import.meta.url).href}" class="h-7 w-9 object-contain" alt="Fina"></span>
          <span><span class="block text-sm font-semibold">ກູ້ເງິນກັບ Fina</span><span class="service-caption">ສະໝັກສິນເຊື່ອງ່າຍໆ</span></span>
        </button>

        <button type="button" class="service-card bg-[#e5fbf4] focus-visible:outline-teal-500 text-[#177c68]" on:click={handleDriverTaxMenu}>
          <span class="service-icon bg-white"><img src="{new URL('@/assets/driver/tax.png', import.meta.url).href}" class="h-8 w-8 object-contain" alt=""></span>
          <span><span class="block text-sm font-semibold">ໃບຮັບຮູ້ອາກອນ</span><span class="service-caption">ປະຫວັດການເສຍອາກອນ</span></span>
        </button>

        {#if $isDriverSalaryMenuVisible}
        <button type="button" class="service-card bg-[#fff7ee] focus-visible:outline-sky-400" on:click={handleDriverSalaryMenu}>
          <span class="service-icon bg-white"><img src="{new URL('@/assets/driver/salary.png', import.meta.url).href}" class="h-8 w-8 object-contain" alt=""></span>
          <span><span class="block text-sm font-semibold">ໃບແຈ້ງເງິນເດືອນ</span><span class="service-caption">ຂໍ້ມູນລາຍໄດ້</span></span>
        </button>
        {/if}



        <button type="button" class="service-card bg-[#e7f7ff] focus-visible:outline-sky-400" on:click={handleCoupon}>
          <span class="service-icon bg-white"><img src="{new URL('@/assets/driver/coupon.png', import.meta.url).href}" class="h-8 w-8 object-contain" alt=""></span>
          <span><span class="block text-sm font-semibold">ບັດສາກລົດໄຟຟ້າ</span><span class="service-caption">ຄູປອງຂອງຂ້ອຍທັງໝົດ</span></span>
        </button>

        <button type="button" class="service-card bg-white focus-visible:outline-teal-500" on:click={handleAdsRegisterMenu}>
          <span class="service-icon bg-white"><img src="{new URL('@/assets/driver/menu-ads-register.png', import.meta.url).href}" class="h-10 w-10 object-contain" alt=""></span>
          <span><span class="block text-sm font-semibold">{adsRegistrationText.title}</span><span class="service-caption">ລົງທະບຽນຮັບລາຍໄດ້</span></span>
        </button>
      </div>
    </section>
  </main>

  <!-- External Loan Warning Dialog -->
  <ExternalLoanWarning
    isOpen={isShowExternalLoanPage}
    on:close={() => (isShowExternalLoanPage = false)}
    on:agree={() => (isShowExternalLoanBrowser = true)}
  />

  <!-- External Loan In-App Browser -->
  <ExternalLoanBrowser
    isOpen={isShowExternalLoanBrowser}
    serviceUrl={externalLoanUrl}
    on:close={() => (isShowExternalLoanBrowser = false)}
  />

</div>

<style lang="scss">
  .payment-card {
    background:
      radial-gradient(circle at 100% 100%, rgba(255, 255, 255, 0.24) 0 25%, transparent 26%),
      linear-gradient(135deg, #21b9f5 0%, #41c1f4 100%);
  }

  .wallet-card {
    background:
      radial-gradient(circle at 100% 100%, rgba(255, 255, 255, 0.23) 0 25%, transparent 26%),
      linear-gradient(135deg, #20cbbb 0%, #16aea5 100%);
  }

  .service-card {
    display: flex;
    min-height: 6rem;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    padding: 0.75rem;
    text-align: left;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
    transition: transform 150ms ease;
  }

  .service-card:active { transform: scale(0.98); }
  .service-card:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }

  .service-icon {
    display: flex;
    height: 2.75rem;
    width: 2.75rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  }

  .service-caption {
    display: block;
    margin-top: 0.125rem;
    color: #94a3b8;
    font-size: 10px;
    line-height: 0.75rem;
  }
</style>
